"use client";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Item } from "@/lib/types";
import { useLocation } from "@/contexts/LocationContext";
import { useAuth } from "@/contexts/AuthContext";
import { useItemShareCard } from "@/lib/hooks/useItemShareCard";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import {
  filterItemsByDiscoveryGranularity,
  resolveEffectiveDiscoveryGranularity,
  HOMEPAGE_DISCOVERY_SHELF_MIN_ITEMS,
} from "@/lib/items/homepageDiscoveryGranularity";
import {
  getUserStateAndLGA,
  getLocationTier,
  orderItemsByTier,
  type LocationTier,
} from "@/lib/locationHierarchy";
import { MapPinIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import ItemCard from "./ItemCard";

// Global lock so Home distance calculations cannot overlap even if React
// remounts components in dev (StrictMode) or multiple module instances exist.
const GLOBAL_HOME_DISTANCE_IN_FLIGHT_KEY = "__sio_homeDistanceInFlight__";

const getHomeDistanceInFlight = (): boolean => {
  return (
    (globalThis as unknown as Record<string, unknown>)[
      GLOBAL_HOME_DISTANCE_IN_FLIGHT_KEY
    ] === true
  );
};

const setHomeDistanceInFlight = (value: boolean) => {
  (globalThis as unknown as Record<string, unknown>)[
    GLOBAL_HOME_DISTANCE_IN_FLIGHT_KEY
  ] = value;
};

interface LocationBasedItemsProps {
  items: Item[];
  title: string;
  subtitle?: string;
  viewAllLink?: string;
  onItemClick?: (item: Item) => void; // Optional click handler for modal mode
  /** Cap how many items get distance calculation (e.g. 20 on home for faster load). Omit = no cap. */
  maxItemsForDistance?: number;
}

interface ItemWithDistance extends Item {
  distance?: number;
}

export default function LocationBasedItems({
  items,
  title,
  subtitle,
  viewAllLink,
  onItemClick,
  maxItemsForDistance,
}: LocationBasedItemsProps) {
  const { userLocation, isLoadingLocation, calculateDistancesAsync } =
    useLocation();
  const {
    includeOtherLocationTier,
    homepageDiscoveryGranularity,
    homepageDiscoveryAutoWidenEnabled,
  } = useSystemSettings();
  const { isAuthenticated, user } = useAuth();
  const itemShare = useItemShareCard();
  const [itemsWithDistances, setItemsWithDistances] = useState<
    ItemWithDistance[]
  >([]);
  const [isCalculatingDistances, setIsCalculatingDistances] = useState(false);

  const homeDistRunSeqRef = useRef(0);
  const homeDistInFlightRef = useRef(0);

  const applyDiscoveryMaxReach = useCallback(
    (rows: ItemWithDistance[]): ItemWithDistance[] => {
      if (!userLocation?.areaName?.trim()) {
        return rows;
      }
      const locationForHierarchy = {
        areaName: userLocation.areaName ?? "",
        formattedAddress: userLocation.formattedAddress,
      };
      // Widen the location reach one tier at a time (e.g. LGA → state) when the
      // configured granularity leaves the shelf thin, so nearer same-state items
      // surface before any out-of-state spillover.
      const effectiveGranularity = resolveEffectiveDiscoveryGranularity(
        rows,
        locationForHierarchy,
        {
          configured: homepageDiscoveryGranularity,
          autoWiden: homepageDiscoveryAutoWidenEnabled,
          minCount: HOMEPAGE_DISCOVERY_SHELF_MIN_ITEMS,
          includeOtherLocationTier,
        },
      );
      return filterItemsByDiscoveryGranularity(
        rows,
        locationForHierarchy,
        effectiveGranularity,
        includeOtherLocationTier,
      );
    },
    [
      userLocation,
      homepageDiscoveryGranularity,
      homepageDiscoveryAutoWidenEnabled,
      includeOtherLocationTier,
    ],
  );

  // Debug instrumentation for mount/unmount removed.

  // Filter items to show Lagos items when user has no location
  const lagosItems = useMemo(() => {
    if (userLocation) return [];
    // Filter items that contain "Lagos" in their location string
    // Handle various formats: "Lagos", "Lagos, Nigeria", "Lekki, Lagos", etc.
    return items
      .filter((item) => {
        const locationLower = item.location.toLowerCase();
        return (
          locationLower.includes("lagos") ||
          locationLower.includes("lagos state") ||
          locationLower.startsWith("lagos")
        );
      })
      .sort((a, b) => {
        // Sort by newest first when no location
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      });
  }, [items, userLocation]);

  // Calculate distances and sort items when user has location
  useEffect(() => {
    const calculateAllDistances = async () => {
      const filterOutOtherTier = (raw: Item[]): Item[] => {
        if (includeOtherLocationTier || !userLocation) return raw;
        const { state: userState, lga: userLGA } = getUserStateAndLGA({
          areaName: userLocation.areaName ?? "",
          formattedAddress: userLocation.formattedAddress,
        });
        const areaName = userLocation.areaName ?? "";
        return raw.filter(
          (item) =>
            getLocationTier(
              item.location ?? "",
              areaName,
              userState,
              userLGA,
            ) !== "other",
        );
      };

      if (!userLocation?.coordinates || items.length === 0) {
        setItemsWithDistances(
          applyDiscoveryMaxReach(
            items.length === 0 ? [] : filterOutOtherTier(items),
          ),
        );
        return;
      }

      // Prevent overlapping distance calculations for the same component instance.
      // Runtime evidence showed Home was re-entering while an earlier run was in-flight,
      // which triggers duplicate `/api/maps/distance-matrix` calls.
      // Debug instrumentation removed: previously logged guard/lock checks.

      if (getHomeDistanceInFlight()) {
        return;
      }

      // Acquire the in-flight lock immediately (before any expensive filtering)
      // so a second effect run cannot pass the guard while the first is still preparing.
      setHomeDistanceInFlight(true);
      homeDistRunSeqRef.current += 1;
      homeDistInFlightRef.current = 1;
      // Filter items that have coordinates and need distance calculation
      const itemsWithCoords = items.filter((item) => item.coordinates);
      const itemsWithoutCoords = items.filter((item) => !item.coordinates);

      if (itemsWithCoords.length === 0) {
        setItemsWithDistances(
          applyDiscoveryMaxReach(filterOutOtherTier(items)),
        );
        homeDistInFlightRef.current -= 1;
        return;
      }

      const { state: userState, lga: userLGA } = getUserStateAndLGA({
        areaName: userLocation.areaName ?? "",
        formattedAddress: userLocation.formattedAddress,
      });
      const areaName = userLocation.areaName ?? "";

      const getTier = (item: Item): LocationTier =>
        getLocationTier(item.location ?? "", areaName, userState, userLGA);

      // R4 progressive render: paint the shelf in tier order immediately so the
      // buyer sees items without waiting on the distance-matrix round-trip. The
      // list below is refined (within-tier distance sort) once distances resolve.
      setItemsWithDistances(
        applyDiscoveryMaxReach(
          orderItemsByTier(items, {
            areaName,
            formattedAddress: userLocation.formattedAddress,
          }) as ItemWithDistance[],
        ),
      );

      // Optional cap: take items in tier order (area → lga → state → other) up to maxItemsForDistance
      let coordsToUse = itemsWithCoords;
      let restCoordsNoDistance: Item[] = [];
      if (
        maxItemsForDistance != null &&
        maxItemsForDistance > 0 &&
        itemsWithCoords.length > maxItemsForDistance
      ) {
        const byTier: Record<LocationTier, Item[]> = {
          area: [],
          lga: [],
          state: [],
          other: [],
        };
        for (const item of itemsWithCoords) {
          byTier[getTier(item)].push(item);
        }
        const ordered = [
          ...byTier.area,
          ...byTier.lga,
          ...byTier.state,
          ...byTier.other,
        ];
        coordsToUse = ordered.slice(0, maxItemsForDistance);
        restCoordsNoDistance = ordered.slice(maxItemsForDistance);
      }

      setIsCalculatingDistances(true);
      try {
        // Calculate distances (for subset when maxItemsForDistance is set)
        const distances = await calculateDistancesAsync(
          userLocation.coordinates,
          coordsToUse.map((item) => item.coordinates!),
        );
        const itemsWithDistance: ItemWithDistance[] = coordsToUse.map(
          (item, index) => ({
            ...item,
            distance: distances[index],
          }),
        );

        // Partition by tier (area → lga → state → other), sort each by distance
        const byTier: Record<LocationTier, ItemWithDistance[]> = {
          area: [],
          lga: [],
          state: [],
          other: [],
        };
        for (const item of itemsWithDistance) {
          byTier[getTier(item)].push(item);
        }

        const sortByDistance = (a: ItemWithDistance, b: ItemWithDistance) => {
          if (a.distance !== undefined && b.distance !== undefined) {
            return a.distance - b.distance;
          }
          if (a.distance !== undefined) return -1;
          if (b.distance !== undefined) return 1;
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        };
        for (const tier of ["area", "lga", "state", "other"] as const) {
          byTier[tier].sort(sortByDistance);
        }

        // Items without coords: partition by tier and append in tier order
        const noCoordsByTier: Record<LocationTier, ItemWithDistance[]> = {
          area: [],
          lga: [],
          state: [],
          other: [],
        };
        for (const item of itemsWithoutCoords) {
          const tier = getTier(item);
          noCoordsByTier[tier].push({ ...item, distance: undefined });
        }

        const restNoDistance = restCoordsNoDistance.map((item) => ({
          ...item,
          distance: undefined,
        }));

        let allItems: ItemWithDistance[] = [
          ...byTier.area,
          ...byTier.lga,
          ...byTier.state,
          ...byTier.other,
          ...noCoordsByTier.area,
          ...noCoordsByTier.lga,
          ...noCoordsByTier.state,
          ...noCoordsByTier.other,
          ...restNoDistance,
        ];

        if (!includeOtherLocationTier) {
          allItems = allItems.filter((item) => getTier(item) !== "other");
        }

        setItemsWithDistances(applyDiscoveryMaxReach(allItems));
      } catch (error) {
        console.error("[LOCATION_ITEMS] Error calculating distances:", error);
        // Fallback: use items without distances
        setItemsWithDistances(applyDiscoveryMaxReach(items));
      } finally {
        setIsCalculatingDistances(false);
        homeDistInFlightRef.current = 0;
        setHomeDistanceInFlight(false);
      }
    };

    calculateAllDistances();
  }, [
    items,
    userLocation,
    calculateDistancesAsync,
    maxItemsForDistance,
    title,
    includeOtherLocationTier,
    homepageDiscoveryGranularity,
    applyDiscoveryMaxReach,
  ]);

  // Determine which items to display
  const displayItems = useMemo((): ItemWithDistance[] => {
    if (userLocation) {
      // Show items sorted by distance (already sorted in itemsWithDistances)
      return itemsWithDistances.slice(0, 20); // Limit to 20 items for single row
    } else {
      // Show Lagos items when no location, or fallback to all items if no Lagos items
      const itemsToShow =
        lagosItems.length > 0
          ? lagosItems.slice(0, 20)
          : items.slice(0, 20).sort((a, b) => {
              // Sort by newest first as fallback
              return (
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
              );
            });
      // Ensure all items have the distance property (undefined for items without location)
      return itemsToShow.map((item) => ({
        ...item,
        distance: undefined,
      }));
    }
  }, [userLocation, itemsWithDistances, lagosItems, items]);

  /**
   * Home shows only the first 20 sorted items. The old logic only inserted a
   * banner when an "other"-tier item appeared inside that slice; if the first
   * 20 were all area/lga/state but index 20+ were "other", no banner showed.
   * We scan the full sorted list for the first "other" and either insert
   * inline (when it falls in the preview) or show a trailing card after the row.
   */
  const locationBannerPlacement = useMemo(():
    | { mode: "inline"; insertIndex: number }
    | { mode: "trailing" }
    | null => {
    if (!includeOtherLocationTier) return null;
    if (!userLocation) return null;
    if (displayItems.length === 0 || itemsWithDistances.length === 0) {
      return null;
    }

    const { state: userState, lga: userLGA } = getUserStateAndLGA({
      areaName: userLocation.areaName ?? "",
      formattedAddress: userLocation.formattedAddress,
    });
    const areaName = userLocation.areaName ?? "";

    let firstOtherIndex: number | null = null;
    for (let i = 0; i < itemsWithDistances.length; i += 1) {
      const tier = getLocationTier(
        itemsWithDistances[i].location ?? "",
        areaName,
        userState,
        userLGA,
      );
      if (tier === "other") {
        firstOtherIndex = i;
        break;
      }
    }

    if (firstOtherIndex === null || firstOtherIndex === 0) {
      return null;
    }

    if (firstOtherIndex < displayItems.length) {
      return { mode: "inline", insertIndex: firstOtherIndex };
    }

    return { mode: "trailing" };
  }, [
    displayItems,
    itemsWithDistances,
    userLocation,
    includeOtherLocationTier,
  ]);

  // R4 progressive render: only full-block when there is nothing to show yet
  // (detecting location, or the very first tier-order paint hasn't landed). Once
  // items are on screen, distance refinement happens in the background with a
  // non-blocking inline hint — no more "Calculating distances..." wall.
  const hasNothingToShow = displayItems.length === 0;
  if (isLoadingLocation || (isCalculatingDistances && hasNothingToShow)) {
    return (
      <div className="w-full">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-semibold">{title}</h2>
              {subtitle ? (
                <p className="mt-1 text-sm text-text-light">{subtitle}</p>
              ) : null}
            </div>
            {viewAllLink && (
              <Link href={viewAllLink} className="text-primary hover:underline">
                View all
              </Link>
            )}
          </div>
        </div>
        <div className="flex items-center justify-center py-10 bg-secondary rounded-lg">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            <p className="text-text-light">
              {isLoadingLocation
                ? "Detecting your location..."
                : "Calculating distances..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="">
            <h2 className="text-2xl font-semibold">{title}</h2>
            {subtitle ? (
              <p className="mt-1 text-sm text-text-light">{subtitle}</p>
            ) : userLocation ? (
              <div className="flex items-center text-sm text-text-light">
                <span> In {userLocation.areaName}</span>
              </div>
            ) : (
              <div className="flex items-center text-sm text-text-light">
                <MapPinIcon className="h-4 w-4 mr-1 text-primary" />
                <span>in Lagos</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isCalculatingDistances && (
              <span
                role="status"
                aria-live="polite"
                className="flex items-center gap-1.5 text-xs text-text-light"
              >
                <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary" />
                Refining by distance…
              </span>
            )}
            {viewAllLink && (
              <Link
                href={viewAllLink}
                className="text-primary hover:underline text-sm"
              >
                View all
              </Link>
            )}
          </div>
        </div>

        {/* Intelligent location prompt */}
        {(!isAuthenticated || !user?.location) && (
          <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-primary-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">
                  💡 See items near you
                </p>
                <p className="text-xs text-text-light">
                  {!isAuthenticated
                    ? "Sign in to get items near you based on your location"
                    : !user?.location
                      ? "Add your location to your profile to see items close to you"
                      : "We couldn't detect your location. Please update it in your profile"}
                </p>
              </div>
              <div className="ml-4">
                {!isAuthenticated ? (
                  <Link
                    href="/login"
                    className="inline-flex items-center px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors shadow-sm"
                  >
                    Sign In
                  </Link>
                ) : (
                  <Link
                    href="/profile"
                    className="inline-flex items-center px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors shadow-sm"
                  >
                    {user?.location ? "Update Location" : "Set Location"}
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Show items in a single horizontal row */}
      {displayItems.length > 0 ? (
        <div className="relative">
          <div className="overflow-x-auto pb-4 hide-scrollbar">
            <div className="flex gap-6" style={{ minWidth: "min-content" }}>
              {(() => {
                const placement = locationBannerPlacement;

                const bannerCard = (variant: "inline" | "trailing") =>
                  userLocation ? (
                    <div className="relative w-[280px] flex-shrink-0">
                      <div className="h-full rounded-lg border border-primary/30 bg-primary/10 px-4 py-6 flex flex-col justify-center">
                        <p className="text-sm font-semibold text-foreground">
                          End of items near you
                        </p>
                        <p className="text-xs text-text-light mt-1">
                          {variant === "trailing"
                            ? "This row only shows nearby listings. Items from other locations are next in the full list — open Market to see them."
                            : "From here on, listings are from other locations."}
                        </p>
                        <p className="text-[11px] text-text-light mt-2">
                          Your area: {userLocation.areaName ?? "your location"}
                        </p>
                      </div>
                    </div>
                  ) : null;

                if (placement?.mode === "inline") {
                  const bannerIndex = placement.insertIndex;
                  const before = displayItems.slice(0, bannerIndex);
                  const after = displayItems.slice(bannerIndex);
                  return (
                    <>
                      {before.map((item, index) => (
                        <div
                          key={item.id}
                          className="relative w-[280px] flex-shrink-0"
                        >
                          <ItemCard
                            item={item}
                            distance={item.distance}
                            index={index}
                            priority={index < 6}
                            onClick={
                              onItemClick ? () => onItemClick(item) : undefined
                            }
                            itemShareUrl={
                              itemShare.showItemShare
                                ? itemShare.buildItemShareUrl(item.id) ||
                                  undefined
                                : undefined
                            }
                            partnerShareToolsEnabled={
                              itemShare.showItemShare &&
                              itemShare.partnerShareToolsEnabled
                            }
                          />
                        </div>
                      ))}
                      {bannerCard("inline")}
                      {after.map((item, i) => {
                        const index = bannerIndex + i;
                        return (
                          <div
                            key={item.id}
                            className="relative w-[280px] flex-shrink-0"
                          >
                            <ItemCard
                              item={item}
                              distance={item.distance}
                              index={index}
                              priority={index < 6}
                              onClick={
                                onItemClick
                                  ? () => onItemClick(item)
                                  : undefined
                              }
                              itemShareUrl={
                                itemShare.showItemShare
                                  ? itemShare.buildItemShareUrl(item.id) ||
                                    undefined
                                  : undefined
                              }
                              partnerShareToolsEnabled={
                                itemShare.showItemShare &&
                                itemShare.partnerShareToolsEnabled
                              }
                            />
                          </div>
                        );
                      })}
                    </>
                  );
                }

                if (placement?.mode === "trailing") {
                  return (
                    <>
                      {displayItems.map((item, index) => (
                        <div
                          key={item.id}
                          className="relative w-[280px] flex-shrink-0"
                        >
                          <ItemCard
                            item={item}
                            distance={item.distance}
                            index={index}
                            priority={index < 6}
                            onClick={
                              onItemClick ? () => onItemClick(item) : undefined
                            }
                            itemShareUrl={
                              itemShare.showItemShare
                                ? itemShare.buildItemShareUrl(item.id) ||
                                  undefined
                                : undefined
                            }
                            partnerShareToolsEnabled={
                              itemShare.showItemShare &&
                              itemShare.partnerShareToolsEnabled
                            }
                          />
                        </div>
                      ))}
                      {bannerCard("trailing")}
                    </>
                  );
                }

                return displayItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="relative w-[280px] flex-shrink-0"
                  >
                    <ItemCard
                      item={item}
                      distance={item.distance}
                      index={index}
                      priority={index < 6}
                      onClick={
                        onItemClick ? () => onItemClick(item) : undefined
                      }
                      itemShareUrl={
                        itemShare.showItemShare
                          ? itemShare.buildItemShareUrl(item.id) || undefined
                          : undefined
                      }
                      partnerShareToolsEnabled={
                        itemShare.showItemShare &&
                        itemShare.partnerShareToolsEnabled
                      }
                    />
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-10 bg-secondary rounded-lg">
          <p className="text-text-light">
            {userLocation
              ? "No items found nearby."
              : "No items available in Lagos at the moment."}
          </p>
        </div>
      )}
    </div>
  );
}
